import {
  CurrencyCode,
  enumFinancialAccountType,
  enumPaymentAllocationMethod,
  enumRoundingMethod,
  enumRoundingPrecision,
  FinancialAccountType,
  PaymentAllocationMethod,
  RoundingMethod,
  RoundingPrecision,
} from '@rematter/frontend-lib-api-client';
import { Checkbox, MoneyField, Select, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $financialAccountForm,
  $updateFinancialAccountForm,
} from '~/modules/finance/_state/financialAccountForm.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';
import { FinancialAccountFormData } from '~/modules/finance/types';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

const SEC_CODE_OPTIONS = [
  { key: 'CCD', label: 'CCD' },
  { key: 'PPD', label: 'PPD' },
  { key: 'CTX', label: 'CTX' },
  { key: 'WEB', label: 'WEB' },
];

type FinancialAccountFormProps = {
  isEditing?: boolean;
};

export const FinancialAccountForm = ({
  isEditing,
}: FinancialAccountFormProps) => {
  const t = useTranslations();
  const form = useAtomValue($financialAccountForm);
  const updateForm = useSetAtom($updateFinancialAccountForm);

  const handleFieldChange =
    <K extends keyof FinancialAccountFormData>(field: K) =>
    (value: FinancialAccountFormData[K] | undefined) => {
      updateForm({
        ...form,
        [field]: value,
      });
    };

  return (
    <div className="@FinancialAccountForm flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.financialAccounts.form.fields.accountName}
          isRequired
          value={form.name}
          onChange={handleFieldChange('name')}
        />
        <SelectEnum<FinancialAccountType>
          label={t.financialAccounts.form.fields.accountType}
          enumValue={enumFinancialAccountType}
          value={form.type}
          onChange={handleFieldChange('type')}
          placeholder={t.financialAccounts.form.fields.accountTypePlaceholder}
        />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <SelectFacility
          label={t.financialAccounts.form.fields.facility}
          value={form.facilityId ?? undefined}
          onChange={(value) => handleFieldChange('facilityId')(value ?? null)}
          isRequired
          isDisabled={isEditing}
        />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <MoneyField
          label={t.financialAccounts.form.fields.initialBalance}
          isRequired
          currencyCode={form.currency}
          amount={form.currentBalance}
          onAmountChange={(value: number) =>
            handleFieldChange('currentBalance')(value)
          }
          onCurrencyChange={(code?: string) =>
            code && handleFieldChange('currency')(code as CurrencyCode)
          }
          isDisabled={isEditing}
          currencyLocked={isEditing}
          aria-description={
            isEditing
              ? t.financialAccounts.form.fields.initialBalanceDisabled
              : undefined
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SelectEnum<RoundingMethod>
          enumValue={enumRoundingMethod}
          value={form.roundingMethod}
          onChange={handleFieldChange('roundingMethod')}
          placeholder={
            t.financialAccounts.form.fields.roundingMethodPlaceholder
          }
        />
        <SelectEnum<RoundingPrecision>
          enumValue={enumRoundingPrecision}
          value={form.roundingPrecision}
          onChange={handleFieldChange('roundingPrecision')}
          placeholder={
            t.financialAccounts.form.fields.roundingPrecisionPlaceholder
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <SelectEnum<PaymentAllocationMethod>
          label={t.financialAccounts.form.fields.paymentAllocationMethod}
          enumValue={enumPaymentAllocationMethod}
          value={form.paymentAllocationMethod}
          onChange={handleFieldChange('paymentAllocationMethod')}
          placeholder={
            t.financialAccounts.form.fields.paymentAllocationMethodPlaceholder
          }
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          isSelected={form.visibleInPayments}
          onChange={handleFieldChange('visibleInPayments')}
        >
          {t.financialAccounts.form.fields.visibleInPayments}
        </Checkbox>
      </div>

      {(form.type === enumFinancialAccountType.Checking ||
        form.type === enumFinancialAccountType.Savings) && (
        <div className="flex flex-col gap-4">
          <span className="text-sm font-medium text-neutral/60">
            {t.financialAccounts.form.fields.nachaConfig}
          </span>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={t.financialAccounts.form.fields.nachaCompanyIdentification}
              value={form.nachaCompanyIdentification}
              onChange={handleFieldChange('nachaCompanyIdentification')}
            />
            <TextField
              label={t.financialAccounts.form.fields.nachaCompanyName}
              value={form.nachaCompanyName}
              onChange={handleFieldChange('nachaCompanyName')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <Select
              label={t.financialAccounts.form.fields.nachaDefaultSecCode}
              placeholder={
                t.financialAccounts.form.fields.nachaDefaultSecCodePlaceholder
              }
              value={form.nachaDefaultSecCode || undefined}
              onChange={handleFieldChange('nachaDefaultSecCode')}
              options={SEC_CODE_OPTIONS}
            />
          </div>
        </div>
      )}
    </div>
  );
};
